import { Search, X } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { Ticket, TicketCategory, TicketStatus } from '@/types/ticket';
import {
  TICKET_CATEGORY_LABELS,
  TICKET_CATEGORIES,
  TICKET_STATUS_LABELS,
  ticketStatusChipClass,
} from '@/components/tickets/ticketLabels';
import { formatTicketInboxRelativeTime } from '@/lib/ticket-datetime';

const INBOX_FILTER_SELECT_CLASS =
  'h-9 min-w-0 w-full rounded-2xl border border-border bg-background px-3 text-xs font-bold text-foreground outline-none focus:border-ring focus:ring-4 focus:ring-ring/20';

export type InboxSortOrder = 'newest' | 'oldest';

function isStudentWaitingForReply(ticket: Ticket): boolean {
  if (ticket.status === 'closed') return false;
  if (ticket.lastMessageSenderType === 'student') return true;
  if (ticket.lastMessageSenderType === 'admin') return false;
  // Fallback for older API responses without lastMessageSenderType
  if (ticket.status === 'replied') return false;
  return true;
}

function inboxSnippet(ticket: Ticket): string {
  if (ticket.lastMessagePreview?.trim()) return ticket.lastMessagePreview;
  switch (ticket.status) {
    case 'just_created':
      return 'New ticket awaiting review.';
    case 'in_process':
      return 'Admin is reviewing this ticket.';
    case 'replied':
      return 'Waiting for student response.';
    case 'closed':
      return 'Ticket closed.';
    default:
      return ticket.description?.slice(0, 90) || 'No messages yet.';
  }
}

function InboxCategoryChip({ category }: { category: Ticket['category'] }) {
  return (
    <span className="inline-flex rounded-full bg-accent px-2.5 py-1 text-[11px] font-bold text-accent-foreground">
      {TICKET_CATEGORY_LABELS[category] ?? category}
    </span>
  );
}

function InboxStatusChip({ status }: { status: TicketStatus }) {
  const { chip, dot } = ticketStatusChipClass(status);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold',
        chip
      )}
    >
      <span className={cn('h-2 w-2 shrink-0 rounded-full', dot)} aria-hidden />
      {TICKET_STATUS_LABELS[status] ?? status}
    </span>
  );
}

function MetaSeparator() {
  return <span className="inline-block h-4 w-px shrink-0 bg-border" aria-hidden />;
}

function TicketInboxRowSkeleton({ titleWidth }: { titleWidth: string }) {
  return (
    <div
      className="grid w-full grid-cols-[1fr_auto] gap-2.5 border-b border-border border-l-4 border-l-transparent px-4 py-4"
      aria-hidden
    >
      <div className="min-w-0 space-y-2">
        <Skeleton className={cn('h-4 max-w-full', titleWidth)} />
        <Skeleton className="h-3 w-44 max-w-[85%]" />
        <Skeleton className="h-3 w-full max-w-[92%]" />
        <div className="flex flex-wrap gap-1.5 pt-0.5">
          <Skeleton className="h-6 w-[72px] rounded-full" />
          <Skeleton className="h-6 w-[88px] rounded-full" />
        </div>
      </div>
      <Skeleton className="h-3 w-9 shrink-0" />
    </div>
  );
}

function TicketInboxListSkeleton() {
  const titleWidths = ['w-[88%]', 'w-[72%]', 'w-[94%]', 'w-[65%]', 'w-[80%]', 'w-[76%]'];
  return (
    <div aria-busy="true" aria-label="Loading tickets">
      {titleWidths.map((width, i) => (
        <TicketInboxRowSkeleton key={i} titleWidth={width} />
      ))}
    </div>
  );
}

type TicketInboxSidebarProps = {
  tickets: Ticket[];
  selectedId: string | null;
  loading: boolean;
  filterStatus: TicketStatus | '';
  filterCategory: TicketCategory | '';
  sortOrder: InboxSortOrder;
  search: string;
  onSearchChange: (value: string) => void;
  onFilterStatusChange: (status: TicketStatus | '') => void;
  onFilterCategoryChange: (category: TicketCategory | '') => void;
  onSortOrderChange: (sort: InboxSortOrder) => void;
  onSelectTicket: (ticketId: string) => void;
  onRestoreTicket?: (ticketId: string) => void;
};

export function TicketInboxSidebar({
  tickets,
  selectedId,
  loading,
  filterStatus,
  filterCategory,
  sortOrder,
  search,
  onSearchChange,
  onFilterStatusChange,
  onFilterCategoryChange,
  onSortOrderChange,
  onSelectTicket,
  onRestoreTicket,
}: TicketInboxSidebarProps) {
  const countLabel = `${tickets.length} ${tickets.length === 1 ? 'Ticket' : 'Tickets'}`;

  return (
    <aside className="flex min-h-0 flex-col overflow-hidden border-r bg-background">
      <div className="shrink-0 border-b px-4 py-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-lg font-bold tracking-tight">Ticket Inbox</h2>
          <span className="shrink-0 rounded-full bg-accent px-3 py-1.5 text-xs font-bold text-accent-foreground">
            {countLabel}
          </span>
        </div>
        <div className="relative mb-2.5">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search title, student, email, ticket #…"
            className={cn(INBOX_FILTER_SELECT_CLASS, 'pl-9 pr-9 font-medium')}
            aria-label="Search tickets"
          />
          {search.trim() ? (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="absolute top-1/2 right-2.5 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <select
            value={filterCategory}
            onChange={(e) => onFilterCategoryChange(e.target.value as TicketCategory | '')}
            className={INBOX_FILTER_SELECT_CLASS}
            aria-label="Filter by category"
          >
            <option value="">All categories</option>
            {TICKET_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {TICKET_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => onFilterStatusChange(e.target.value as TicketStatus | '')}
            className={INBOX_FILTER_SELECT_CLASS}
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            {(['just_created', 'in_process', 'replied', 'closed'] as const).map((s) => (
              <option key={s} value={s}>
                {TICKET_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          <select
            value={sortOrder}
            onChange={(e) => onSortOrderChange(e.target.value as InboxSortOrder)}
            className={cn(INBOX_FILTER_SELECT_CLASS, 'col-span-2')}
            aria-label="Sort tickets"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto bg-background">
        {loading ? (
          <TicketInboxListSkeleton />
        ) : tickets.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm leading-relaxed text-muted-foreground">
            {search.trim()
              ? 'No tickets match your search or filters.'
              : 'No tickets match the selected inbox filters.'}
          </div>
        ) : (
          tickets.map((ticket) => {
            const isActive = selectedId === ticket.ticketId;
            const isUnread = Boolean(ticket.unreadForAdmin);
            const showReplyBell = isUnread && !isActive;
            const activityAt =
              ticket.timestamps?.lastMessageAt ??
              ticket.timestamps?.createdAt ??
              undefined;

            return (
              <button
                key={ticket.ticketId}
                type="button"
                onClick={() => onSelectTicket(ticket.ticketId)}
                className={cn(
                  'grid w-full grid-cols-[1fr_auto] gap-2.5 border-b border-border border-l-4 px-4 py-4 text-left transition-colors',
                  isActive
                    ? 'border-l-primary bg-accent'
                    : 'border-l-transparent hover:bg-muted/50',
                  isUnread && !isActive && 'bg-accent/40'
                )}
              >
                <div className="min-w-0">
                  <span
                    className={cn(
                      'block truncate text-sm text-foreground',
                      isUnread ? 'font-black' : 'font-bold'
                    )}
                  >
                    {ticket.title}
                  </span>
                  <div className="mt-1 text-xs font-bold text-muted-foreground">
                    {ticket.createdBy?.userName ?? 'Student'} · {ticket.ticketId}
                  </div>
                  <div className="mt-1 truncate text-xs text-muted-foreground">
                    {inboxSnippet(ticket)}
                  </div>
                  <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                    <InboxCategoryChip category={ticket.category} />
                    <span className="inline-flex items-center gap-1.5">
                      <MetaSeparator />
                      <InboxStatusChip status={ticket.status} />
                    </span>
                    {isStudentWaitingForReply(ticket) && (
                      <span className="inline-flex max-w-full animate-pulse items-center gap-1.5 rounded-full bg-destructive/10 px-2 py-1 text-[10px] leading-tight font-black tracking-wide text-destructive uppercase">
                        <span className="text-[8px] text-destructive" aria-hidden>
                          ●
                        </span>
                        Student waiting · Reply soon
                      </span>
                    )}
                    {ticket.deletedByStudent && onRestoreTicket && (
                      <span className="inline-flex items-center gap-1.5">
                        <MetaSeparator />
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRestoreTicket(ticket.ticketId);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              onRestoreTicket(ticket.ticketId);
                            }
                          }}
                          className="inline-flex h-7 items-center rounded-full border border-primary/30 bg-background px-2.5 text-[11px] font-bold text-primary hover:bg-accent"
                        >
                          Restore
                        </span>
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex min-w-[42px] flex-col items-end text-right">
                  <span
                    className={cn(
                      'text-xs text-muted-foreground',
                      isUnread && 'font-black text-foreground'
                    )}
                  >
                    {formatTicketInboxRelativeTime(activityAt)}
                  </span>
                  {showReplyBell && (
                    <span
                      className="reply-bell mt-1.5 inline-grid h-6 w-6 place-items-center rounded-full bg-warning/10 text-sm text-warning"
                      title="New student activity"
                      aria-label="New student message"
                    >
                      🔔
                    </span>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </aside>
  );
}
